import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";

export interface ArtikelZeile { id: string; code: string; bezeichnung: string; preisCent: number; status: "im_regal" | "im_warenkorb" | "verkauft" | "rueckgabe_kiste" | "abgeholt" | "schwund" | "entfernt"; mieterId: string; mieterName: string; mieternummer: string; regalNummer: string; erstelltAt: string; fotoKey: string | null }
export interface Person { id: string; name: string; email: string; rolle: "inhaber" | "mitarbeiter" | "aushilfe"; pinGesetzt: boolean; aktiv: boolean; letzterLoginAt: string | null; totp: boolean }

export const useArtikel = (f: { mieterId?: string; status?: ArtikelZeile["status"]; q?: string }) =>
  useQuery({ queryKey: ["laden", "artikel", f.mieterId ?? "", f.status ?? "", f.q ?? ""], queryFn: () => rufe<{ artikel: ArtikelZeile[] }>(api.api.laden.artikel.$get({ query: { mieterId: f.mieterId, status: f.status, q: f.q || undefined } })), placeholderData: (alt) => alt });
export const usePersonal = () => useQuery({ queryKey: ["laden", "personal"], queryFn: () => rufe<{ personen: Person[] }>(api.api.laden.personal.$get()) });

function invalidiere(keys: string[][]) {
  return () => {
    const qc = useQueryClient();
    return (d?: unknown) => {
      for (const k of keys) void qc.invalidateQueries({ queryKey: k });
      return d;
    };
  };
}

export function useArtikelAnlegen() {
  const nach = invalidiere([["laden", "artikel"], ["laden", "mieter"], ["laden", "uebersicht"]])();
  return useMutation({ mutationFn: (d: { mieterId: string; regalId: string; bezeichnung: string; preisCent: number; anzahl: number }) => rufe<{ artikel: { id: string; code: string }[] }>(api.api.laden.artikel.$post({ json: { ...d, kategorie: "", groesse: "" } })), onSuccess: nach });
}
export function usePersonAnlegen() {
  const nach = invalidiere([["laden", "personal"]])();
  return useMutation({ mutationFn: (d: { name: string; email: string; rolle: Person["rolle"] }) => rufe<{ id: string }>(api.api.laden.personal.$post({ json: d })), onSuccess: nach });
}
export function usePersonAendern() {
  const nach = invalidiere([["laden", "personal"]])();
  return useMutation({ mutationFn: (d: { benutzerId: string; rolle: Person["rolle"]; aktiv: boolean }) => rufe(api.api.laden.personal[":benutzerId"].$put({ param: { benutzerId: d.benutzerId }, json: { rolle: d.rolle, aktiv: d.aktiv } })), onSuccess: nach });
}
export function useEinladungSenden() {
  return useMutation({ mutationFn: (benutzerId: string) => rufe(api.api.laden.personal[":benutzerId"].einladung.$post({ param: { benutzerId } })) });
}
export function usePinSetzen() {
  const nach = invalidiere([["laden", "personal"]])();
  return useMutation({ mutationFn: (d: { benutzerId: string; pin: string }) => rufe(api.api.laden.personal[":benutzerId"].pin.$post({ param: { benutzerId: d.benutzerId }, json: { pin: d.pin } })), onSuccess: nach });
}

/** Etiketten-PDF holen und in neuem Tab öffnen (Druckdialog des Browsers). */
export async function etikettenOeffnen(artikelIds: string[], format: "62x29" | "a4", startfeld = 1): Promise<void> {
  const res = await api.api.laden.etiketten.$post({ json: { artikelIds, format, startfeld } });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { fehler?: string };
    throw new Error(body.fehler ?? "Etiketten konnten nicht erzeugt werden.");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const fenster = window.open(url, "_blank");
  if (!fenster) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `etiketten-${format}.pdf`;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
